import { FormBlock } from '@qbe/ads-blocks-forms';
import { FormGroupLabel, TextField, Typography } from '@qbe/ads-core';
import React from 'react';
import {
  Controller,
  ControllerFieldState,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';

import { useAppContent, useAppDispatch } from '~/hooks';
import { useAbn } from '~/hooks/useAbn';
import { qalTrackEventAction } from '~/thunks/analytics';
import { TextFieldState } from '~/types/enums';
import { analyticsConfig } from '~/utils/analytics/analyticsConfig';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

interface BusinessNameProps<T extends FieldValues> extends UseControllerProps<T> {
  showLabel?: boolean;
}

const specialChars = [
  '!',
  '"',
  '#',
  '$',
  '%',
  '&',
  "'",
  '(',
  ')',
  '*',
  ',',
  '-',
  '.',
  '/',
  ':',
  ';',
  '=',
  '?',
  '@',
  '\\',
  '_',
  '{',
  '}',
]
  .map((c) => `\\${c}`)
  .join('|');

const asicCharacterSet = new RegExp(`^(?:${specialChars}|[a-zA-Z\\d\\s])+$`);
const maxLength = 100;

const BusinessName = <T extends FieldValues>({
  control,
  name,
  showLabel = true,
}: BusinessNameProps<T>) => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.businessNameComponent',
  });

  const {
    field: { onChange },
  } = useController({
    control,
    name,
  });
  const { businessNameLookup } = useAbn();
  const dispatch = useAppDispatch();

  const onChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    onChange(e);
    // Analytics - validate whether abn look up is triggered and user continuous update the business name.
    if (analyticsConfig.abnLookupStatus) {
      dispatch(
        qalTrackEventAction({
          eventAction: 'edit business name after abn lookup success',
          eventLabel: 'edit business name after abn lookup success',
        })
      );
      analyticsConfig.abnLookUpStatus = false;
    }
  };

  const getFieldState = (value: string, fieldState: ControllerFieldState) => {
    if (fieldState.error) return TextFieldState.Error;
    if (!fieldState.isTouched) return TextFieldState.Default;
    if (businessNameLookup.value && businessNameLookup.value === value)
      return TextFieldState.Success;
    return TextFieldState.Default;
  };

  const contextualHelpDescription = getJSXContent('contextualhelpers.businessName.description');

  return (
    <>
      {showLabel && (
        <FormGroupLabel
          id="business-name"
          title={getTextContent('fields.businessName.title')}
          subtitle={getTextContent('fields.businessName.description')}
          alignCenter
          titleSuffix={
            <ContextualHelpWithButton tracking="help: What's the name of your business?">
              <Typography>{contextualHelpDescription}</Typography>
            </ContextualHelpWithButton>
          }
        />
      )}

      <FormBlock preset="input" maxWidth="100%">
        <Controller
          name={name}
          control={control}
          render={({ field: { ref, ...field }, fieldState }) => {
            const hasError = getFieldState(field.value, fieldState) === TextFieldState.Error;
            return (
              <TextField
                {...field}
                id="businessName"
                inputRef={ref}
                onChange={onChangeHandler}
                label={getTextContent('fields.businessName.label')}
                data-track="Business Name"
                data-track-value="mask"
                helperText={
                  hasError
                    ? getTextContent('fields.businessName.invalidText')
                    : getTextContent('fields.businessName.helperText')
                }
                error={hasError}
                displayIcon
                required
                disabled={businessNameLookup.isFetching}
              />
            );
          }}
          rules={{
            pattern: asicCharacterSet,
            required: true,
            maxLength,
          }}
        />
      </FormBlock>
    </>
  );
};

export default BusinessName;
